export interface ScreenResolution {
  width: number;
  height: number;
}

export interface ScreenSourceHandlers {
  onShareEnded?: () => void;
}

export class ScreenSource {
  private stream: MediaStream | null = null;

  constructor(private readonly video: HTMLVideoElement) {}

  async start(handlers: ScreenSourceHandlers = {}): Promise<ScreenResolution> {
    if (!navigator.mediaDevices?.getDisplayMedia) {
      throw new Error('getDisplayMedia is not supported in this browser.');
    }

    this.stop();
    this.video.removeAttribute('src');
    this.video.load();
    this.video.loop = false;

    this.stream = await navigator.mediaDevices.getDisplayMedia({
      video: { frameRate: { ideal: 30 } },
      audio: false,
    });

    const track = this.stream.getVideoTracks()[0];
    track.addEventListener('ended', () => {
      handlers.onShareEnded?.();
    });

    this.video.srcObject = this.stream;
    await this.waitForData();
    await this.video.play();

    const settings = track.getSettings();
    return {
      width: settings.width ?? this.video.videoWidth,
      height: settings.height ?? this.video.videoHeight,
    };
  }

  stop(): void {
    if (!this.stream) return;
    for (const t of this.stream.getTracks()) t.stop();
    this.stream = null;
    this.video.srcObject = null;
  }

  private waitForData(): Promise<void> {
    return new Promise((resolve) => {
      if (this.video.readyState >= 2) {
        resolve();
        return;
      }
      this.video.onloadeddata = () => resolve();
    });
  }
}

export function extractYouTubeId(url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed) return null;
  try {
    const u = new URL(trimmed);
    const host = u.hostname.replace(/^www\./, '');
    if (host === 'youtu.be') {
      return u.pathname.slice(1) || null;
    }
    if (host === 'youtube.com' || host === 'm.youtube.com') {
      if (u.pathname === '/watch') return u.searchParams.get('v');
      const shortsMatch = /^\/shorts\/([\w-]+)/.exec(u.pathname);
      if (shortsMatch) return shortsMatch[1];
      const embedMatch = /^\/embed\/([\w-]+)/.exec(u.pathname);
      if (embedMatch) return embedMatch[1];
    }
    return null;
  } catch {
    return null;
  }
}

export function normalizeYouTubeUrl(url: string): string | null {
  const id = extractYouTubeId(url);
  return id ? `https://www.youtube.com/watch?v=${id}` : null;
}

export function toYouTubeEmbedUrl(url: string): string | null {
  const id = extractYouTubeId(url);
  return id ? `https://www.youtube.com/embed/${id}?autoplay=1&rel=0` : null;
}
